import { useState, useEffect, useCallback } from "react"
import { getBrowserSupabaseClient } from "@/lib/supabase/client"
import styles from "./logs-page.module.css"
import { useToast } from "@/client/components/common/toast"

interface AdminLog {
  id: string
  admin_id: string
  action: string
  target_type: string | null
  target_id: string | null
  details: string | null
  created_at: string
  admin_nickname?: string
}

const PAGE_SIZE = 20

const ACTION_LABELS: Record<string, string> = {
  create_user: "创建用户",
  delete_user: "删除用户",
  update_email: "修改邮箱",
  update_role: "变更角色",
  update_level: "调整等级",
  freeze_user: "冻结用户",
  unfreeze_user: "解冻用户",
  reset_password: "重置密码",
  adjust_balance: "调整余额",
}

export default function AdminLogsPage() {
  const toast = useToast()
  const [logs, setLogs] = useState<AdminLog[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [actionFilter, setActionFilter] = useState<string>("all")

  const fetchLogs = useCallback(async () => {
    const supabase = getBrowserSupabaseClient()
    if (!supabase) { setLoading(false); return }

    setLoading(true)
    try {
      let query = supabase
        .from("admin_logs")
        .select("id, admin_id, action, target_type, target_id, details, created_at", { count: "exact" })
        .order("created_at", { ascending: false })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1)

      if (actionFilter !== "all") {
        query = query.eq("action", actionFilter)
      }

      const { data, count } = await query

      const adminIds = [...new Set((data ?? []).map((l) => l.admin_id))]
      const { data: admins } = await supabase
        .from("users")
        .select("id, nickname")
        .in("id", adminIds)

      const adminMap = new Map((admins ?? []).map((a) => [a.id, a.nickname]))

      const rows: AdminLog[] = (data ?? []).map((l) => ({
        ...l,
        admin_nickname: adminMap.get(l.admin_id) ?? "未知",
      }))

      setLogs(rows)
      setTotalCount(count ?? rows.length)
    } catch {
      toast.error("加载操作日志失败")
    } finally {
      setLoading(false)
    }
  }, [page, actionFilter, toast])

  useEffect(() => { fetchLogs() }, [fetchLogs])

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>操作日志</h1>
        <div className={styles.actions}>
          <select
            className={styles.filterSelect}
            value={actionFilter}
            onChange={(e) => { setActionFilter(e.target.value); setPage(0) }}
          >
            <option value="all">全部操作</option>
            {Object.entries(ACTION_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <button className={styles.refreshBtn} onClick={() => fetchLogs()} disabled={loading}>
            {loading ? "加载中..." : "刷新"}
          </button>
        </div>
      </div>

      <div className={styles.tableContainer}>
        <div className={styles.tableHeader}>
          <span className={styles.colTime}>时间</span>
          <span className={styles.colAdmin}>操作者</span>
          <span className={styles.colAction}>操作</span>
          <span className={styles.colTarget}>目标</span>
          <span className={styles.colDetails}>详情</span>
        </div>
        {logs.length === 0 ? (
          <div className={styles.empty}>{loading ? "加载中..." : "暂无日志记录"}</div>
        ) : (
          logs.map((log) => (
            <div key={log.id} className={styles.tableRow}>
              <span className={styles.colTime}>
                {new Date(log.created_at).toLocaleString("zh-CN", {
                  month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
                })}
              </span>
              <span className={styles.colAdmin}>{log.admin_nickname}</span>
              <span className={styles.colAction}>
                <span className={styles.actionTag}>{ACTION_LABELS[log.action] ?? log.action}</span>
              </span>
              <span className={styles.colTarget}>{log.target_type ?? "—"}{log.target_id ? ` ${log.target_id.slice(0, 8)}…` : ""}</span>
              <span className={styles.colDetails}>{log.details ?? "—"}</span>
            </div>
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button disabled={page === 0} onClick={() => setPage((p) => p - 1)}>上一页</button>
          <span>{page + 1} / {totalPages}</span>
          <button disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>下一页</button>
        </div>
      )}
    </div>
  )
}
